/**
 * Loads per-scenario node materials and supplemental nodes for every scenario in the project.
 * Prevents cross-scenario leakage from nodeId-only maps or primary-scenario-only hydration.
 */
import type { INodeDto } from "@/types/INodeDto";
import { createNodeMaterialKey } from "@/types/LCAoM/ModelsDataService";
import {
  GetScenarioNodeDataBatchRequest,
  NodeMaterialDto,
  NodeSupplementalDto,
} from "@api/apiClient";
import { nodesClient } from "@api/models";
import { QueryClient, useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

/** React Query key prefix for Results tab per-scenario material/supplemental batch loads. */
export const projectAnalysisScenarioNodeDataQueryKey = {
  root: ["projectAnalysis", "scenarioNodeData"] as const,
  project: (projectId: string) => ["projectAnalysis", "scenarioNodeData", projectId] as const,
  scenario: (projectId: string, scenarioId: string) =>
    ["projectAnalysis", "scenarioNodeData", projectId, scenarioId] as const,
};

/**
 * Marks Results tab scenario node data stale and refetches in the background.
 * Call after Assign (or similar) mutates materials for a single scenario.
 */
export async function invalidateProjectAnalysisScenarioNodeData(
  queryClient: QueryClient,
  projectId: string,
  scenarioId: string,
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: projectAnalysisScenarioNodeDataQueryKey.scenario(projectId, scenarioId),
  });
}

export type ScenarioNodesByScenarioModelKey = `${string}:${string}`;

export function createScenarioModelNodesKey(scenarioId: string, modelId: string): ScenarioNodesByScenarioModelKey {
  return `${scenarioId}:${modelId}`;
}

export interface UseAllScenariosProjectNodeDataParams {
  projectId: string | undefined;
  /** All scenario IDs to load (typically every scenario in the project). */
  scenarioIds: string[];
  /** Model IDs to fetch per scenario (models linked to that scenario). */
  modelIdsByScenario: Map<string, string[]>;
}

export interface UseAllScenariosProjectNodeDataResult {
  nodeMaterialLookup: Map<string, NodeMaterialDto>;
  /** Supplemental nodes from API, keyed by `${scenarioId}:${modelId}`. */
  scenarioNodesByScenarioModel: Map<ScenarioNodesByScenarioModelKey, INodeDto[]>;
  /** Supplemental metadata keyed by `${scenarioId}:${nodeId}`. */
  nodeSupplementalLookup: Map<string, NodeSupplementalDto>;
  materialsLoadedScenarioIds: ReadonlySet<string>;
  isFetching: boolean;
  isSuccess: boolean;
}

export function useAllScenariosProjectNodeData({
  projectId,
  scenarioIds,
  modelIdsByScenario,
}: UseAllScenariosProjectNodeDataParams): UseAllScenariosProjectNodeDataResult {
  const queries = useQueries({
    queries: scenarioIds.map((scenarioId) => {
      const modelIds = modelIdsByScenario.get(scenarioId) ?? [];
      return {
        queryKey: [...projectAnalysisScenarioNodeDataQueryKey.scenario(projectId!, scenarioId), modelIds] as const,
        queryFn: async ({ signal }: { signal: AbortSignal }) => {
          if (!modelIds.length) {
            return { scenarioId, nodeMaterials: {}, nodes: {} };
          }
          const response = await nodesClient.getScenarioNodeDataBatch(
            new GetScenarioNodeDataBatchRequest({ modelIds, scenarioId }),
            signal,
          );
          return {
            scenarioId,
            nodeMaterials: response.nodeMaterials ?? {},
            nodes: response.nodes ?? {},
            nodeSupplementals: response.nodeSupplementals ?? {},
          };
        },
        enabled: !!projectId && modelIds.length > 0,
        staleTime: 5 * 60 * 1000,
      };
    }),
  });

  return useMemo(() => {
    const nodeMaterialLookup = new Map<string, NodeMaterialDto>();
    const scenarioNodesByScenarioModel = new Map<ScenarioNodesByScenarioModelKey, INodeDto[]>();
    const nodeSupplementalLookup = new Map<string, NodeSupplementalDto>();
    const materialsLoadedScenarioIds = new Set<string>();

    queries.forEach((query) => {
      if (!query.isSuccess || !query.data) return;

      const { scenarioId, nodeMaterials, nodes, nodeSupplementals } = query.data;
      materialsLoadedScenarioIds.add(scenarioId);

      for (const [modelId, materials] of Object.entries(nodeMaterials)) {
        for (const material of materials ?? []) {
          if (material.nodeId != null) {
            nodeMaterialLookup.set(createNodeMaterialKey(scenarioId, material.nodeId), material);
          }
        }
      }

      for (const [modelId, supplementalNodes] of Object.entries(nodes)) {
        if (supplementalNodes?.length) {
          scenarioNodesByScenarioModel.set(
            createScenarioModelNodesKey(scenarioId, modelId),
            supplementalNodes as INodeDto[],
          );
        }
      }

      for (const [, supplementals] of Object.entries(nodeSupplementals ?? {})) {
        for (const supplemental of supplementals ?? []) {
          if (supplemental.nodeId != null) {
            nodeSupplementalLookup.set(`${scenarioId}:${supplemental.nodeId}`, supplemental);
          }
        }
      }
    });

    const isFetching = queries.some((q) => q.isFetching);
    const isSuccess =
      scenarioIds.length === 0 ||
      queries.every((q) => q.isSuccess || q.fetchStatus === "idle");

    return {
      nodeMaterialLookup,
      scenarioNodesByScenarioModel,
      nodeSupplementalLookup,
      materialsLoadedScenarioIds,
      isFetching,
      isSuccess,
    };
  }, [queries, scenarioIds]);
}
